import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useMemo } from 'react'
import Card, { CardHeader } from '../../../components/ui/Card'
import Badge from '../../../components/ui/Badge'
import Button from '../../../components/ui/Button'
import Spinner from '../../../components/ui/Spinner'
import Modal from '../../../components/ui/Modal'
import api from '../../../lib/api'
import { formatCurrency, formatDate } from '../../../lib/formatters'
import type { WorkOrder, WorkOrderStatus, LabProfile, WorkOrderItem, Settings } from '../../../types'
import toast from 'react-hot-toast'

// Default Print Header content
const DEFAULT_PRINT_HEADER = `{{LabName}}
Dr. N Sivasangkar B.D.S || Dr. S Nithya B.D.S
No: 130 Kovai Road, Karur, 639002 Phone: 9344104051`

// Max items per page for print layout (compact to fit single page)
const ITEMS_PER_PAGE = 15

const STATUS_FLOW: WorkOrderStatus[] = ['draft', 'received', 'in_progress', 'completed', 'delivered']

// FDI (Fédération Dentaire Internationale) Numbering System
// Quadrant layout from viewer's perspective (looking at patient):
// Q1 Upper Right: 18,17,16,15,14,13,12,11 | Q2 Upper Left: 21,22,23,24,25,26,27,28
// Q4 Lower Right: 48,47,46,45,44,43,42,41 | Q3 Lower Left: 31,32,33,34,35,36,37,38

interface QuadrantData {
  q1: number[]  // Upper Right: 11-18
  q2: number[]  // Upper Left: 21-28
  q3: number[]  // Lower Left: 31-38
  q4: number[]  // Lower Right: 41-48
}

function parseTeethToQuadrants(toothNumbers?: string): QuadrantData {
  const result: QuadrantData = { q1: [], q2: [], q3: [], q4: [] }

  if (!toothNumbers || !toothNumbers.trim()) return result

  const parts = toothNumbers.split(/[\s,]+/).map(s => s.trim()).filter(Boolean)
  const numbers = parts.map(p => parseInt(p, 10)).filter(n => !isNaN(n))

  for (const n of numbers) {
    // FDI notation: first digit is quadrant, second is tooth position
    if (n >= 11 && n <= 18) result.q1.push(n)
    else if (n >= 21 && n <= 28) result.q2.push(n)
    else if (n >= 31 && n <= 38) result.q3.push(n)
    else if (n >= 41 && n <= 48) result.q4.push(n)
  }

  // Sort for display:
  // Q1: descending (18,17,16,15,14,13,12,11) - from back to front
  // Q2: ascending (21,22,23,24,25,26,27,28) - from front to back
  // Q3: ascending (31,32,33,34,35,36,37,38) - from front to back
  // Q4: descending (48,47,46,45,44,43,42,41) - from back to front
  result.q1.sort((a, b) => b - a)
  result.q2.sort((a, b) => a - b)
  result.q3.sort((a, b) => a - b)
  result.q4.sort((a, b) => b - a)

  return result
}

function TeethQuadrantGrid({ toothNumbers, compact = false }: { toothNumbers?: string; compact?: boolean }) {
  const { q1, q2, q3, q4 } = parseTeethToQuadrants(toothNumbers)
  const hasAny = q1.length || q2.length || q3.length || q4.length
  if (!hasAny) return <span className="text-gray-400">-</span>
  
  // Check if all teeth are selected (32 total)
  const totalTeeth = q1.length + q2.length + q3.length + q4.length
  const isAllTeeth = totalTeeth >= 32
  
  if (isAllTeeth) {
    return (
      <span 
        className="inline-flex items-center justify-center px-2 py-1 text-xs font-semibold rounded bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
        title="All 32 teeth selected"
      >
        All
      </span>
    )
  }
  
  const labelStyle: React.CSSProperties = {
    fontSize: compact ? '5pt' : '6pt',
    fontWeight: 600,
    color: '#666',
    padding: '0 2px',
  }
  
  const cellStyle: React.CSSProperties = {
    border: '0.5pt solid #ccc',
    padding: compact ? '1px 3px' : '2px 4px',
    fontSize: compact ? '6pt' : '7pt',
    fontFamily: 'monospace',
    textAlign: 'center',
    minWidth: compact ? 18 : 24,
    verticalAlign: 'middle',
  }
  
  return (
    <table style={{ borderCollapse: 'collapse', display: 'inline-table', margin: 0 }} className="teeth-quadrant-grid">
      <tbody>
        <tr>
          <td style={cellStyle}>
            <span style={labelStyle}>Q1</span> {q1.length > 0 ? q1.join(',') : '–'}
          </td>
          <td style={cellStyle}>
            <span style={labelStyle}>Q2</span> {q2.length > 0 ? q2.join(',') : '–'}
          </td>
        </tr>
        <tr>
          <td style={cellStyle}>
            <span style={labelStyle}>Q4</span> {q4.length > 0 ? q4.join(',') : '–'}
          </td>
          <td style={cellStyle}>
            <span style={labelStyle}>Q3</span> {q3.length > 0 ? q3.join(',') : '–'}
          </td>
        </tr>
      </tbody>
    </table>
  )
}

export default function WorkOrderDetailPage() {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [showEditModal, setShowEditModal] = useState(false)
  const [editPriority, setEditPriority] = useState('')
  const [editReceivedDate, setEditReceivedDate] = useState('')
  const [editDueDate, setEditDueDate] = useState('')
  const [editNotes, setEditNotes] = useState('')
  const [editPatientName, setEditPatientName] = useState('')
  const [editTrackingNumber, setEditTrackingNumber] = useState('')
  const [editCourierName, setEditCourierName] = useState('')
  const [editEstimatedDelivery, setEditEstimatedDelivery] = useState('')
  const [editSpecialInstructions, setEditSpecialInstructions] = useState('')
  const [editTechnicianId, setEditTechnicianId] = useState('')

  const { data: order, isLoading } = useQuery({
    queryKey: ['work-orders', id],
    queryFn: async () => {
      const response = await api.get(`/work-orders/${id}`)
      // API returns { data: workOrder }, so we need response.data.data
      return response.data?.data as WorkOrder
    },
  })

  const { data: labProfile } = useQuery({
    queryKey: ['settings', 'lab-profile'],
    queryFn: async () => {
      const { data } = await api.get<LabProfile>('/settings/lab-profile')
      return data
    },
  })

  // Fetch print header from settings
  const { data: allSettings } = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: async () => {
      try {
        const { data } = await api.get<{ data: Settings[] }>('/settings')
        return data?.data || []
      } catch {
        return []
      }
    },
  })

  // Get print header text, replacing {{LabName}} with actual lab name
  const getPrintHeader = () => {
    const headerSetting = allSettings?.find((s: Settings) => s.key === 'print_header_text')
    const headerText = headerSetting?.value || DEFAULT_PRINT_HEADER
    const labName = labProfile?.name || 'Jasmine Dental Lab'
    return headerText.replace(/\{\{LabName\}\}/g, labName)
  }

  const printHeaderLines = getPrintHeader().split('\n')

  // Fetch users for assignment dropdown
  interface User {
    id: string
    name: string
    email: string
    role: string
  }
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      try {
        const { data } = await api.get('/users')
        return data
      } catch {
        return { data: [] }
      }
    },
  })
  const users: User[] = usersData?.data || []

  // Fetch invoices for this work order
  interface WorkOrderInvoice {
    id: string
    displayId: string
    issueDate: string
    totalAmount: number
    balanceAmount: number
    status: string
  }
  const { data: workOrderInvoices } = useQuery({
    queryKey: ['work-order-invoices', id],
    queryFn: async () => {
      try {
        const { data } = await api.get(`/invoices?workOrderId=${id}`)
        return (data?.data || []) as WorkOrderInvoice[]
      } catch {
        return []
      }
    },
    enabled: !!id,
  })

  // Chunk items into pages for printing (max 10 items per page)
  const itemPages = useMemo(() => {
    if (!order?.items) return []
    const pages: WorkOrderItem[][] = []
    for (let i = 0; i < order.items.length; i += ITEMS_PER_PAGE) {
      pages.push(order.items.slice(i, i + ITEMS_PER_PAGE))
    }
    return pages
  }, [order?.items])

  const totalPages = Math.max(1, itemPages.length)

  const updateStatusMutation = useMutation({
    mutationFn: async (newStatus: WorkOrderStatus) => {
      return api.put(`/work-orders/${id}/status`, { status: newStatus })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders', id] })
      toast.success('Status updated successfully')
    },
    onError: () => {
      toast.error('Failed to update status')
    },
  })

  const updateOrderMutation = useMutation({
    mutationFn: async (updates: {
      priority?: string
      received_date?: string
      due_date?: string
      notes?: string
      patient_name?: string
      tracking_number?: string
      courier_name?: string
      estimated_delivery?: string
      special_instructions?: string
      technician_id?: string
    }) => {
      return api.put(`/work-orders/${id}`, updates)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders', id] })
      setShowEditModal(false)
      toast.success('Order updated successfully')
    },
    onError: () => {
      toast.error('Failed to update order')
    },
  })

  const createInvoiceMutation = useMutation({
    mutationFn: async () => {
      return api.post('/invoices', { workOrderId: id })
    },
    onSuccess: (response) => {
      const invoiceId = response.data?.data?.id
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['work-order-invoices', id] })
      toast.success('Invoice created successfully')
      if (invoiceId) {
        navigate(`/invoices/${invoiceId}`)
      } else {
        navigate('/invoices')
      }
    },
    onError: (error: any) => {
      const errorData = error.response?.data
      if (errorData?.code === 'INVOICE_ALREADY_EXISTS') {
        const invoiceNumber = errorData.existingInvoiceNumber || 'Unknown'
        const invoiceId = errorData.existingInvoiceId
        toast.error(
          <div>
            <p>An active invoice ({invoiceNumber}) already exists for this work order.</p>
            {invoiceId && (
              <button
                onClick={() => navigate(`/invoices/${invoiceId}`)}
                className="text-primary-400 hover:text-primary-300 underline mt-1"
              >
                View Invoice
              </button>
            )}
          </div>,
          { duration: 5000 }
        )
      } else if (errorData?.code === 'DATABASE_MIGRATION_REQUIRED') {
        toast.error('Database needs to be updated. Please contact admin to run migration 007.', { duration: 8000 })
      } else {
        const detail = errorData?.detail || 'Failed to create invoice'
        toast.error(detail)
      }
    },
  })

  const duplicateOrderMutation = useMutation({
    mutationFn: async () => {
      return api.post(`/work-orders/${id}/duplicate`)
    },
    onSuccess: (response) => {
      const newOrderId = response.data?.data?.id
      queryClient.invalidateQueries({ queryKey: ['work-orders'] })
      toast.success('Order duplicated successfully')
      if (newOrderId) {
        navigate(`/work-orders/${newOrderId}`)
      }
    },
    onError: () => {
      toast.error('Failed to duplicate order')
    },
  })

  const handlePrint = () => {
    window.print()
  }

  const handleOpenEdit = () => {
    if (order) {
      setEditPriority(order.priority || 'normal')
      setEditReceivedDate(order.receivedDate ? order.receivedDate.split('T')[0] : '')
      setEditDueDate(order.dueDate ? order.dueDate.split('T')[0] : '')
      setEditNotes(order.notes || '')
      setEditPatientName((order as any).patient_name || order.patientName || '')
      setEditTrackingNumber((order as any).tracking_number || '')
      setEditCourierName((order as any).courier_name || '')
      setEditEstimatedDelivery((order as any).estimated_delivery ? (order as any).estimated_delivery.split('T')[0] : '')
      setEditSpecialInstructions((order as any).special_instructions || '')
      setEditTechnicianId((order as any).technician_id || '')
      setShowEditModal(true)
    }
  }

  const handleSaveEdit = () => {
    updateOrderMutation.mutate({
      priority: editPriority,
      received_date: editReceivedDate || undefined,
      due_date: editDueDate,
      notes: editNotes,
      patient_name: editPatientName || undefined,
      tracking_number: editTrackingNumber || undefined,
      courier_name: editCourierName || undefined,
      estimated_delivery: editEstimatedDelivery || undefined,
      special_instructions: editSpecialInstructions || undefined,
      technician_id: editTechnicianId || undefined,
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!order) {
    return (
      <div className="text-center py-12">
        <i className="fas fa-clipboard-list text-4xl text-gray-400 mb-4"></i>
        <p className="text-gray-500 dark:text-gray-400">Work order not found</p>
        <Link to="/work-orders" className="text-primary-600 hover:underline mt-2 inline-block">
          Back to Work Orders
        </Link>
      </div>
    )
  }

  const getStatusBadge = (status: WorkOrderStatus) => {
    const variants: Record<WorkOrderStatus, 'success' | 'warning' | 'error' | 'info' | 'gray'> = {
      draft: 'gray',
      received: 'info',
      in_progress: 'warning',
      completed: 'success',
      delivered: 'success',
      cancelled: 'error',
    }
    const label = status ? String(status).replace('_', ' ') : 'unknown'
    return <Badge variant={variants[status] || 'gray'}>{label}</Badge>
  }

  const getPriorityBadge = (priority: string) => {
    const variants: Record<string, 'error' | 'warning' | 'info' | 'gray'> = {
      urgent: 'error',
      high: 'warning',
      normal: 'info',
      low: 'gray',
    }
    return <Badge variant={variants[priority] || 'gray'}>{priority || 'normal'}</Badge>
  }

  const currentStatusIndex = STATUS_FLOW.indexOf(order.status)
  const nextStatus = STATUS_FLOW[currentStatusIndex + 1]

  const totalAmount = order.items?.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0) ?? 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link
            to="/work-orders"
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <i className="fas fa-arrow-left"></i>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{order.displayId}</h1>
              {getStatusBadge(order.status)}
              {getPriorityBadge(order.priority)}
            </div>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Created {formatDate(order.createdAt)}
            </p>
          </div>
        </div>
        <div className="flex gap-2 no-print">
          {order.status !== 'cancelled' && order.status !== 'delivered' && (
            <>
              {nextStatus && (
                <Button
                  onClick={() => updateStatusMutation.mutate(nextStatus)}
                  loading={updateStatusMutation.isPending}
                >
                  <i className="fas fa-arrow-right mr-2"></i>
                  Mark as {nextStatus ? String(nextStatus).replace('_', ' ') : ''}
                </Button>
              )}
              <Button variant="secondary" onClick={handleOpenEdit}>
                <i className="fas fa-edit mr-2"></i>
                Edit
              </Button>
            </>
          )}
          <Button variant="secondary" onClick={handlePrint}>
            <i className="fas fa-print mr-2"></i>
            Print
          </Button>
        </div>
      </div>

      {/* Status Timeline */}
      <Card className="no-print">
        <CardHeader title="Order Progress" />
        <div className="flex items-center justify-between">
          {STATUS_FLOW.map((status, index) => {
            const isActive = index <= currentStatusIndex
            const isCurrent = status === order.status
            return (
              <div key={status} className="flex items-center flex-1">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isActive
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                    } ${isCurrent ? 'ring-4 ring-primary-200 dark:ring-primary-900' : ''}`}
                  >
                    {isActive ? (
                      <i className="fas fa-check"></i>
                    ) : (
                      <span className="text-sm">{index + 1}</span>
                    )}
                  </div>
                  <span className={`text-xs mt-2 capitalize ${isActive ? 'text-primary-600 font-medium' : 'text-gray-500'}`}>
                    {status ? String(status).replace('_', ' ') : ''}
                  </span>
                </div>
                {index < STATUS_FLOW.length - 1 && (
                  <div
                    className={`flex-1 h-1 mx-2 ${
                      index < currentStatusIndex
                        ? 'bg-primary-600'
                        : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                  />
                )}
              </div>
            )
          })}
        </div>
      </Card>

      {/* Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 no-print">
        {/* Doctor Info */}
        <Card>
          <CardHeader title="Doctor / Client" />
          <div className="space-y-2">
            <p className="font-medium text-gray-900 dark:text-white">{order.doctor?.name}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">{order.doctor?.clinicName}</p>
            <Link
              to={`/doctors/${order.doctorId}`}
              className="text-sm text-primary-600 hover:underline inline-flex items-center gap-1"
            >
              View profile <i className="fas fa-external-link-alt text-xs"></i>
            </Link>
          </div>
        </Card>

        {/* Patient Info */}
        <Card>
          <CardHeader title="Patient" />
          {order.patientName ? (
            <div className="space-y-2">
              <p className="font-medium text-gray-900 dark:text-white">{order.patientName}</p>
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400">No patient specified</p>
          )}
        </Card>

        {/* Assignment & Dates */}
        <Card>
          <CardHeader title="Assignment & Dates" />
          <div className="space-y-2">
            <p className="text-sm text-gray-600 dark:text-gray-400">Assigned to:</p>
            <p className="font-medium text-gray-900 dark:text-white">
              {order.assignedUser?.name || 'Unassigned'}
            </p>
            <p className="text-sm text-gray-500">
              <span className="text-gray-400">Received:</span>{' '}
              <span className="font-medium text-gray-700 dark:text-gray-200">{order.receivedDate ? formatDate(order.receivedDate) : '-'}</span>
            </p>
            <p className="text-sm text-gray-500">
              <span className="text-gray-400">Due:</span>{' '}
              <span className="font-medium text-gray-700 dark:text-gray-200">{formatDate(order.dueDate)}</span>
            </p>
          </div>
        </Card>

        {/* Invoices */}
        <Card>
          <CardHeader 
            title="Invoices" 
            subtitle={workOrderInvoices && workOrderInvoices.length > 0 ? `${workOrderInvoices.length} invoice(s)` : undefined}
          />
          {workOrderInvoices && workOrderInvoices.length > 0 ? (
            <div className="space-y-2">
              {workOrderInvoices.map((invoice) => (
                <Link
                  key={invoice.id}
                  to={`/invoices/${invoice.id}`}
                  className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {invoice.displayId}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {invoice.issueDate ? formatDate(invoice.issueDate) : '-'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {formatCurrency(invoice.totalAmount)}
                    </p>
                    <Badge 
                      variant={
                        invoice.status === 'paid' ? 'success' : 
                        invoice.status === 'cancelled' ? 'error' : 
                        invoice.balanceAmount > 0 ? 'warning' : 'success'
                      }
                    >
                      {invoice.status === 'cancelled' ? 'Cancelled' :
                       invoice.balanceAmount > 0 ? `Due: ${formatCurrency(invoice.balanceAmount)}` : 'Paid'}
                    </Badge>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              No invoices generated yet
            </p>
          )}
        </Card>

        {/* Shipping / Tracking */}
        {(order.tracking_number || order.trackingNumber || order.courier_name || order.courierName || ['completed', 'delivered'].includes(order.status)) && (
          <Card>
            <CardHeader title="Shipping / Tracking" />
            <div className="space-y-3">
              {/* Courier Name */}
              {(order.courier_name || order.courierName) && (
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Courier</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {order.courier_name || order.courierName}
                  </p>
                </div>
              )}

              {/* Tracking Number */}
              {(order.tracking_number || order.trackingNumber) && (
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Tracking Number</p>
                  <p className="font-medium text-gray-900 dark:text-white font-mono">
                    {order.tracking_number || order.trackingNumber}
                  </p>
                </div>
              )}

              {/* Shipped/Delivered dates if available */}
              {(order.shipped_date || order.shippedDate) && (
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Shipped Date</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {formatDate(order.shipped_date || order.shippedDate || '')}
                  </p>
                </div>
              )}

              {order.status === 'delivered' && (
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Status</p>
                  <Badge variant="success">
                    <i className="fas fa-check-circle mr-1"></i>
                    Delivered
                  </Badge>
                </div>
              )}

              {/* Track Shipment Button */}
              {(order.tracking_url || order.trackingUrl || order.tracking_number || order.trackingNumber) && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    const trackingUrl = order.tracking_url || order.trackingUrl
                    const trackingNumber = order.tracking_number || order.trackingNumber
                    const courier = (order.courier_name || order.courierName || '').toLowerCase()
                    
                    // If we have a direct tracking URL, use it
                    if (trackingUrl) {
                      window.open(trackingUrl, '_blank')
                      return
                    }
                    
                    // Otherwise, try to construct a URL based on courier
                    let url = ''
                    if (courier.includes('delhivery')) {
                      url = `https://www.delhivery.com/track/package/${trackingNumber}`
                    } else if (courier.includes('bluedart')) {
                      url = `https://www.bluedart.com/tracking/${trackingNumber}`
                    } else if (courier.includes('dtdc')) {
                      url = `https://www.dtdc.in/tracking/tracking.asp?strCnno=${trackingNumber}`
                    } else if (courier.includes('india post') || courier.includes('speed post')) {
                      url = `https://www.indiapost.gov.in/_layouts/15/dop.portal.tracking/trackconsignment.aspx?CONSESSION_NO=${trackingNumber}`
                    } else if (courier.includes('fedex')) {
                      url = `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`
                    } else if (courier.includes('ekart') || courier.includes('flipkart')) {
                      url = `https://ekartlogistics.com/track/${trackingNumber}`
                    } else {
                      // Generic Google search for tracking
                      url = `https://www.google.com/search?q=${encodeURIComponent(`${courier} tracking ${trackingNumber}`)}`
                    }
                    
                    window.open(url, '_blank')
                  }}
                  className="w-full"
                >
                  <i className="fas fa-truck mr-2"></i>
                  Track Shipment
                </Button>
              )}

              {/* No tracking info message */}
              {!order.tracking_number && !order.trackingNumber && !order.courier_name && !order.courierName && (
                <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                  Tracking information not available
                </p>
              )}
            </div>
          </Card>
        )}
      </div>

      {/* Items */}
      <Card className="no-print">
        <CardHeader title="Order Items" subtitle={`${order.items?.length ?? 0} items`} />
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Teeth</th>
                <th>Shade</th>
                <th className="text-right">Qty</th>
                <th className="text-right">Unit Price</th>
                <th className="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {order.items?.map((item) => (
                <tr key={item.id}>
                  <td>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {item.catalogItem?.name ?? 'Unknown Item'}
                      </p>
                      {item.notes && (
                        <p className="text-sm text-gray-500">{item.notes}</p>
                      )}
                    </div>
                  </td>
                  <td className="text-gray-600 dark:text-gray-300">
                    <TeethQuadrantGrid toothNumbers={item.toothNumbers} />
                  </td>
                  <td className="text-gray-600 dark:text-gray-300">
                    {item.shade || '-'}
                  </td>
                  <td className="text-right text-gray-600 dark:text-gray-300">
                    {item.quantity}
                  </td>
                  <td className="text-right text-gray-600 dark:text-gray-300">
                    {formatCurrency(item.unitPrice)}
                  </td>
                  <td className="text-right font-medium text-gray-900 dark:text-white">
                    {formatCurrency(item.unitPrice * item.quantity)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-200 dark:border-gray-600">
                <td colSpan={5} className="text-right font-semibold text-gray-900 dark:text-white">
                  Subtotal (before GST):
                </td>
                <td className="text-right font-bold text-lg text-gray-900 dark:text-white">
                  {formatCurrency(totalAmount)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>

      {/* Notes */}
      {order.notes && (
        <Card className="no-print">
          <CardHeader title="Notes" />
          <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{order.notes}</p>
        </Card>
      )}

      {/* Actions */}
      <Card className="no-print">
        <CardHeader title="Actions" />
        <div className="flex flex-wrap gap-3">
          {/* Show Create Invoice button only when no active (non-cancelled) invoice exists */}
          {(() => {
            const activeInvoices = (workOrderInvoices || []).filter(inv => inv.status !== 'cancelled')
            const hasActiveInvoice = activeInvoices.length > 0
            return !hasActiveInvoice ? (
              <Button
                variant="secondary"
                onClick={() => createInvoiceMutation.mutate()}
                loading={createInvoiceMutation.isPending}
              >
                <i className="fas fa-file-invoice mr-2"></i>
                Create Invoice
              </Button>
            ) : null
          })()}
          <Button
            variant="secondary"
            onClick={() => duplicateOrderMutation.mutate()}
            loading={duplicateOrderMutation.isPending}
          >
            <i className="fas fa-copy mr-2"></i>
            Duplicate Order
          </Button>
          {order.status !== 'cancelled' && (
            <Button
              variant="danger"
              onClick={() => {
                if (confirm('Are you sure you want to cancel this order?')) {
                  updateStatusMutation.mutate('cancelled')
                }
              }}
              loading={updateStatusMutation.isPending}
            >
              <i className="fas fa-times mr-2"></i>
              Cancel Order
            </Button>
          )}
        </div>
      </Card>

      {/* Edit Order Modal */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="Edit Work Order" size="lg">
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          {/* Basic Information */}
          <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Patient Name
                </label>
                <input
                  type="text"
                  value={editPatientName}
                  onChange={(e) => setEditPatientName(e.target.value)}
                  placeholder="Enter patient name"
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Priority
                </label>
                <select
                  value={editPriority}
                  onChange={(e) => setEditPriority(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Assign To
                </label>
                <select
                  value={editTechnicianId}
                  onChange={(e) => setEditTechnicianId(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Unassigned</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>{user.name} ({user.role})</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Dates */}
          <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Dates</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Received Date
                </label>
                <input
                  type="date"
                  value={editReceivedDate}
                  onChange={(e) => setEditReceivedDate(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Due Date
                </label>
                <input
                  type="date"
                  value={editDueDate}
                  onChange={(e) => setEditDueDate(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Estimated Delivery Date
                </label>
                <input
                  type="date"
                  value={editEstimatedDelivery}
                  onChange={(e) => setEditEstimatedDelivery(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Shipping & Tracking */}
          <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Shipping & Tracking</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Courier Name
                </label>
                <input
                  type="text"
                  value={editCourierName}
                  onChange={(e) => setEditCourierName(e.target.value)}
                  placeholder="e.g., Delhivery, BlueDart, DTDC"
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tracking Number <span className="text-primary-600">*</span>
                </label>
                <input
                  type="text"
                  value={editTrackingNumber}
                  onChange={(e) => setEditTrackingNumber(e.target.value)}
                  placeholder="Enter tracking/AWB number"
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="text-xs text-gray-500 mt-1">Required to track shipment</p>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Notes & Instructions</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Internal Notes
                </label>
                <textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                  placeholder="Internal notes for the team..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Special Instructions
                </label>
                <textarea
                  value={editSpecialInstructions}
                  onChange={(e) => setEditSpecialInstructions(e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                  placeholder="Special handling instructions..."
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700 sticky bottom-0 bg-white dark:bg-gray-800">
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} loading={updateOrderMutation.isPending}>
              <i className="fas fa-save mr-2"></i>
              Save Changes
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── PRINT-ONLY LAYOUT ────────────────────────────────────── */}
      <style>{`
        @media print {
          .wo-print-area * { box-shadow: none !important; filter: none !important; }
          .wo-page { font-size:7.5pt; line-height:1.25; color:#111; page-break-after:always; box-shadow: none !important; }
          .wo-page:last-child { page-break-after:auto; }
          .wo-hdr { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:1.5pt solid #4f46e5; padding-bottom:4px; margin-bottom:6px; box-shadow: none !important; }
          .wo-ftr { display:flex; justify-content:space-between; border-top:0.5pt solid #ccc; padding-top:3px; margin-top:8px; font-size:6.5pt; color:#888; box-shadow: none !important; }
          .wo-tbl { width:100%; border-collapse:collapse; font-size:7pt; box-shadow: none !important; }
          .wo-tbl th { background:#f3f4f6; font-weight:600; text-transform:uppercase; font-size:6.5pt; padding:2px 4px; border:0.5pt solid #ccc; text-align:left; box-shadow: none !important; }
          .wo-tbl td { padding:2px 4px; border:0.5pt solid #ccc; box-shadow: none !important; }
          .wo-tbl .r { text-align:right; }
          .wo-tbl .c { text-align:center; }
          .wo-print-area .teeth-quadrant-grid { box-shadow: none !important; }
          .wo-print-area .teeth-quadrant-grid td { box-shadow: none !important; }
        }
      `}</style>

      <div className="print-only hidden wo-print-area">
        {itemPages.map((pageItems, pageIndex) => {
          const isFirstPage = pageIndex === 0
          const isLastPage = pageIndex === totalPages - 1
          const startIdx = pageIndex * ITEMS_PER_PAGE
          return (
            <div key={pageIndex} className="wo-page">
              {/* Header - Using configurable print header from settings */}
              <div className="wo-hdr">
                <div style={{display:'flex', alignItems:'center', gap:'6px'}}>
                  <div style={{width:24,height:24,background:'#4f46e5',borderRadius:4,display:'flex',alignItems:'center',justifyContent:'center'}}>
                    <i className="fas fa-tooth" style={{color:'#fff',fontSize:'10px'}}></i>
                  </div>
                  <div>
                    {printHeaderLines.map((line, idx) => (
                      <div
                        key={idx}
                        style={{
                          fontWeight: idx === 0 ? 700 : 400,
                          fontSize: idx === 0 ? '9pt' : '6.5pt',
                          color: idx === 0 ? '#111' : '#666'
                        }}
                      >
                        {line}
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{textAlign:'right'}}>
                  <div style={{fontSize:'8pt',fontWeight:700,color:'#4f46e5'}}>WORK ORDER / JOB CARD</div>
                  <div style={{fontSize:'9pt',fontWeight:700}}>{order.displayId}</div>
                  <div style={{fontSize:'6.5pt',color:'#666'}}>Status: {order.status?.replace('_', ' ').toUpperCase()}</div>
                </div>
              </div>

              {/* Order details (first page) */}
              {isFirstPage && (
                <div style={{display:'flex', justifyContent:'space-between', marginBottom:6, fontSize:'7pt'}}>
                  <div>
                    <div style={{fontSize:'6pt',fontWeight:600,textTransform:'uppercase',color:'#888',marginBottom:1}}>Doctor / Client</div>
                    <div style={{fontWeight:600}}>{order.doctor?.name}</div>
                    <div style={{color:'#555',fontSize:'6.5pt'}}>{order.doctor?.clinicName}</div>
                    {order.patientName && <div style={{fontSize:'6.5pt',marginTop:2}}><span style={{color:'#888'}}>Patient:</span> {order.patientName}</div>}
                  </div>
                  <div style={{textAlign:'right',fontSize:'6.5pt'}}>
                    <div><span style={{color:'#888'}}>Received:</span> <span style={{fontWeight:600}}>{order.receivedDate ? formatDate(order.receivedDate) : formatDate(order.createdAt)}</span></div>
                    <div><span style={{color:'#888'}}>Due:</span> <span style={{fontWeight:600}}>{formatDate(order.dueDate)}</span></div>
                    <div><span style={{color:'#888'}}>Priority:</span> {(order.priority || 'Normal').toUpperCase()}</div>
                    {order.assignedUser && <div><span style={{color:'#888'}}>Assigned:</span> {order.assignedUser.name}</div>}
                  </div>
                </div>
              )}
              {!isFirstPage && <div style={{fontSize:'6.5pt',color:'#888',fontStyle:'italic',marginBottom:4}}>...continued</div>}

              {/* Items table */}
              <table className="wo-tbl" style={{marginBottom:6}}>
                <thead>
                  <tr>
                    <th style={{width:'4%'}}>#</th>
                    <th>Item Description</th>
                    <th className="c" style={{width:'12%'}}>Teeth</th>
                    <th className="c" style={{width:'8%'}}>Shade</th>
                    <th className="r" style={{width:'6%'}}>Qty</th>
                    <th className="r" style={{width:'12%'}}>Rate</th>
                    <th className="r" style={{width:'14%'}}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((item, idx) => (
                    <tr key={item.id}>
                      <td>{startIdx + idx + 1}</td>
                      <td>
                        <span style={{fontWeight:500}}>{item.catalogItem?.name ?? 'Unknown Item'}</span>
                        {item.notes && <span style={{fontSize:'6pt',color:'#888',marginLeft:4}}>({item.notes})</span>}
                      </td>
                      <td className="c"><TeethQuadrantGrid toothNumbers={item.toothNumbers} compact /></td>
                      <td className="c">{item.shade || '-'}</td>
                      <td className="r">{item.quantity}</td>
                      <td className="r">{formatCurrency(item.unitPrice)}</td>
                      <td className="r" style={{fontWeight:500}}>{formatCurrency(item.unitPrice * item.quantity)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Summary (last page) */}
              {isLastPage && (
                <div style={{display:'flex',justifyContent:'space-between',gap:16}}>
                  <div style={{flex:1}}>
                    {order.notes && (
                      <div style={{fontSize:'6.5pt',marginBottom:8}}>
                        <div style={{fontWeight:600,marginBottom:1}}>Notes:</div>
                        <div style={{color:'#555',whiteSpace:'pre-wrap'}}>{order.notes}</div>
                      </div>
                    )}
                    {/* Signature areas */}
                    <div style={{display:'flex',justifyContent:'space-between',marginTop:20,gap:16}}>
                      {['Received By','Lab Technician','Quality Check'].map(label => (
                        <div key={label} style={{textAlign:'center',width:80}}>
                          <div style={{borderBottom:'0.5pt solid #999',height:18,marginBottom:2}}></div>
                          <div style={{fontSize:'6pt',color:'#888'}}>{label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{width:160,fontSize:'7pt'}}>
                    <div style={{display:'flex',justifyContent:'space-between',padding:'3px 0',borderTop:'1.5pt solid #111',borderBottom:'1.5pt solid #111',fontWeight:700,fontSize:'7.5pt'}}>
                      <span>Subtotal (before GST)</span><span>{formatCurrency(totalAmount)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="wo-ftr">
                <span>{labProfile?.address}</span>
                <span style={{fontWeight:600}}>{order.displayId} &nbsp;|&nbsp; Page {pageIndex + 1} of {totalPages}</span>
                <span>Printed: {new Date().toLocaleDateString()}</span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
